import { useCallback, useContext, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { orderBy } from 'lodash';
import { color as fplColor } from '@figma/fpl-tokens/tokens.stylex';
import { useCommunityPublishEligibility } from '@/hooks/useCommunityPublishEligibility';
import { useToolListingStatus } from '@/hooks/useToolListingStatus';
import { useLinkedFigmaAccount } from '@/hooks/useLinkedFigmaAccount';
import { useOrgPublishDestinations } from '@/hooks/useOrgPublishDestinations';
import { useFeatureFlag } from '@/hooks/useFeatureFlag';
import { FF_APP_VIEW_SEPARATION, FF_TOOL_COMMUNITY_PUBLISHING } from '@/consts/featureFlags';
import { toolWorkflowPath } from '@/utils/recipes';
import { MENU_NAMES } from '@/consts/menu-names.consts';
import {
  AppDialog,
  AppDialogContent,
  AppDialogHeader,
  AppDialogSection,
  AppDialogTitle,
  AppDialogXBtn,
} from '@/UI/AppDialog/AppDialog';
import { Box } from '@/UI/Box/Box';
import { Tooltip } from '@/UI/Tooltip/Tooltip';
import { Skeleton } from '@/UI/Skeleton/Skeleton';
import { TextSmRg, TextStdMd } from '@/UI/AppTypography/AppTypography';
import { Avatar } from '@/UI/Avatar/Avatar';
import { ButtonContained } from '@/UI/Buttons/AppButton';
import { AppContextMenu } from '@/UI/AppContextMenu/AppContextMenu';
import { Flex, FlexCenVer, FlexCol } from '@/UI/styles';
import { CheckMarkCircleIcon } from '@/UI/Icons';
import { ChevronRightIcon } from '@/UI/Icons/ChevronRightIcon';
import { GoToMainComponentIcon } from '@/UI/Icons/GoToMainComponentIcon';
import { LinkSmallIcon } from '@/UI/Icons/LinkSmallIcon';
import { LockClosedIcon } from '@/UI/Icons/LockClosedIcon';
import { TeamIcon } from '@/UI/Icons/TeamIcon';
import { GlobeIcon } from '@/UI/Icons/GlobeIcon';
import { color, EL_COLORS } from '@/colors';
import { log } from '@/logger/logger.ts';
import { I18N_KEYS } from '@/language/keys';
import { ToolVisibilityType } from '@/enums/tool-visibility-type';
import { useGlobalStore } from '@/state/global.state';
import { sanitizeError } from '@/utils/sanitize-error';
import {
  useDeleteToolSharedUserMutation,
  useSetToolVisibilityMutation,
  useShareToolMutation,
  useToolSharedUsersQuery,
  useToolViewQuery,
} from '@/queries/useToolView';
import { AuthContext } from '@/contexts/AuthContext';
import { useFplMigrationFlag } from '@/contexts/FplMigrationContext';
import { useToolCommunityMetadata } from '@/components/Recipe/FlowComponents/Share/useWorkflowCommunityMetadata';
import { isPublishedToCommunity } from '@/components/Recipe/FlowComponents/Share/workflowCommunityStatus';
import EmailChipsInput from '@/components/Recipe/FlowComponents/Share/EmailChipsInputV2';
import PublishToCommunityPanel from '@/components/Recipe/FlowComponents/Share/PublishToCommunityPanel';
import { StandalonePublishWorkflowProvider } from '@/components/Recipe/FlowComponents/Share/publishWorkflowSource';
import CopyBtnV2 from '@/components/Common/ImageList/CopyBtnV2';
import { useUserWorkflowRole, useWorkflowStore } from '@/state/workflow.state';
import { ToolShareRow } from './ToolShareRow';
import { ToolShareScopeBanner } from './ToolShareScopeBanner';
import { loadingPublishWorkflowSource, useToolPagePublishWorkflowSource } from './useToolPagePublishWorkflowSource';

const logger = log.getLogger('ToolShareModal');

/** The subset of a user the share rows render — satisfied by both `User` and `ToolSharedUser`. */
interface ShareRowUser {
  displayName?: string | null;
  email?: string | null;
  photoURL?: string | null;
}

interface ToolShareModalProps {
  toolId: string;
  /**
   * The workflow editor provides the graph and workflow state publishing reads. Elsewhere (the `/tool`
   * page) the modal fetches the workflow itself, and only the tool's creator can publish.
   */
  inWorkflowEditor?: boolean;
  open: boolean;
  onClose: () => void;
}

export const ToolShareModal = ({ toolId, open, onClose, inWorkflowEditor = false }: ToolShareModalProps) => {
  const { t } = useTranslation();
  const { updateSnackbarData } = useGlobalStore();
  const isFpl = useFplMigrationFlag();
  /** Dropped by `fplIcon` on the FPL path, which draws its replacement at its own 24px box. */
  const headerGlyphSize = isFpl ? 24 : 16;

  const { value: toolPublishEnabled } = useFeatureFlag(FF_TOOL_COMMUNITY_PUBLISHING);
  const { data: tool } = useToolViewQuery(toolId, open);
  const { source: toolPagePublishWorkflow, isLoading: isLoadingToolPagePublishWorkflow } =
    useToolPagePublishWorkflowSource(tool, open && !inWorkflowEditor && toolPublishEnabled === true);
  // Off the editor the store has no workflow role; the fetched workflow says whether this user edits it.
  const publishEligibility = useCommunityPublishEligibility(
    true,
    inWorkflowEditor ? undefined : (toolPagePublishWorkflow?.role ?? 'guest'),
  );
  // The row shows as a skeleton while the workflow loads. It must not fall back to the editor's
  // store meanwhile, which is empty here, so it gets a guest stand-in that can't publish.
  const standalonePublishWorkflow = useMemo(
    () => toolPagePublishWorkflow ?? loadingPublishWorkflowSource(tool?.workflowId ?? '', tool?.name ?? ''),
    [tool?.name, tool?.workflowId, toolPagePublishWorkflow],
  );
  const { destinations: orgDestinations, status: orgLookupStatus } = useOrgPublishDestinations(open);
  // Every unsettled lookup still shows the row: held while loading, to link Figma, or to explain a failure.
  const hasOrgPublishRow =
    orgDestinations.length > 0 ||
    orgLookupStatus === 'loading' ||
    orgLookupStatus === 'unlinked' ||
    orgLookupStatus === 'error';
  // Community's banned, enterprise and unpaid rules don't apply to an org publish; editing still does.
  const hasOrgOption =
    hasOrgPublishRow && publishEligibility.reason !== 'viewer' && publishEligibility.reason !== 'feature_disabled';
  // The org row only shows on its own once eligibility settles (the role isn't known before), but a row
  // already shown for Community gets the orgs right away so it never reads Community first.
  const canPublishToOrg = hasOrgOption && !publishEligibility.isLoading;
  const canOfferPublish =
    toolPublishEnabled === true &&
    (canPublishToOrg ||
      publishEligibility.canShowEntrypoint ||
      publishEligibility.reason === 'banned' ||
      publishEligibility.reason === 'enterprise' ||
      publishEligibility.reason === 'org_member');

  const [emails, setEmails] = useState<string[]>([]);
  const [currentInput, setCurrentInput] = useState({ value: '', isValid: false });

  const { data: shared, isLoading, isError } = useToolSharedUsersQuery(toolId, open);
  const shareMutation = useShareToolMutation(toolId);
  const removeMutation = useDeleteToolSharedUserMutation(toolId);

  const { value: ffEnabled } = useFeatureFlag(FF_APP_VIEW_SEPARATION);
  const setVisibilityMutation = useSetToolVisibilityMutation(toolId);
  const workflowRole = useUserWorkflowRole();
  const canManageListing = inWorkflowEditor ? workflowRole === 'editor' : tool?.isOwner === true;
  // Owners also need the destination to restrict visibility in standalone Share. The listing
  // endpoint still checks workflow edit access; an unsuccessful read keeps Restricted disabled.
  const shouldReadListing = canManageListing || tool?.isOwner === true;
  const { data: figmaConnection } = useLinkedFigmaAccount();
  const isFigmaUnlinked = figmaConnection?.connected === false;
  const {
    data: listing,
    isError: isListingError,
    isOrgScopedPublishingEnabled,
  } = useToolListingStatus(shouldReadListing && open ? toolId : undefined);
  // Editors see the row (as a skeleton) while eligibility loads, so it doesn't pop in once it settles.
  const isResolvingPublishRow =
    toolPublishEnabled === true &&
    canManageListing &&
    (publishEligibility.isLoading || isLoadingToolPagePublishWorkflow);
  // Off the editor a new publish needs the fetched workflow. An existing listing still shows without
  // it, so its owner can unpublish, which needs only the tool.
  const hasPublishWorkflow = inWorkflowEditor || toolPagePublishWorkflow != null;

  const { currentUser } = useContext(AuthContext);
  // Core sends the opt-in to the owner only. Everyone else reads it as off, which is the note they had.
  // In the editor the store has the live value (it flips with the checkbox); elsewhere only the tool view has it.
  const exposesWorkflow = useWorkflowStore((state) =>
    state.recipe.id === tool?.workflowId
      ? state.recipe.visibilityIncludesToolAudience === true
      : tool?.exposesWorkflow === true,
  );
  const [visibilityAnchorEl, setVisibilityAnchorEl] = useState<HTMLElement | null>(null);

  const visibilityOptions = [
    {
      type: ToolVisibilityType.Private,
      text: t(I18N_KEYS.SHARE_WORKFLOW_MODAL.LINK.RESTRICTED),
      icon: <LockClosedIcon width={24} height={24} />,
    },
    {
      type: ToolVisibilityType.Team,
      text: t(I18N_KEYS.SHARE_WORKFLOW_MODAL.LINK.TEAM),
      icon: <TeamIcon width={24} height={24} />,
    },
    {
      type: ToolVisibilityType.Public,
      text: t(I18N_KEYS.SHARE_WORKFLOW_MODAL.LINK.ANYONE),
      icon: <GlobeIcon width={24} height={24} />,
    },
  ];
  const selectedVisibility = visibilityOptions.find((o) => o.type === tool?.visibility) ?? visibilityOptions[0];
  const canManageVisibility = ffEnabled && !!tool?.isOwner;

  // Org listings have no Community creator access. Resolve the destination before reading metadata.
  // Standalone owners may lack workflow edit access for listing, while still belonging to the
  // Community creator profile. Preserve their Community access check if listing fails.
  const shouldFetchCommunityMetadata =
    !shouldReadListing ||
    !isOrgScopedPublishingEnabled ||
    listing?.destination?.type === 'community' ||
    (!inWorkflowEditor && isListingError && listing == null);
  const { data: communityMetadata, refetch: refetchCommunityMetadata } = useToolCommunityMetadata(
    open && shouldFetchCommunityMetadata ? toolId : undefined,
  );
  const isCommunityAccessLocked = toolPublishEnabled === true && isPublishedToCommunity(communityMetadata);
  // The listing read is gated on the org rollout, so a Community listing also counts from its metadata.
  const hasExistingListing = listing?.destination != null || isCommunityAccessLocked;
  const showPublishPanel =
    canManageListing && (isResolvingPublishRow || (hasPublishWorkflow && canOfferPublish) || hasExistingListing);
  const isOrgAccessLocked = shouldReadListing && isOrgScopedPublishingEnabled && listing?.destination?.type === 'org';
  // A hidden destination still confirms an active listing. Since its scope is withheld, keep
  // Public access rather than treating destination:null as proof the tool is unpublished.
  const isListingDestinationHidden =
    shouldReadListing && isOrgScopedPublishingEnabled && listing?.publishing_status === 'restricted';
  // Wait while the Figma connection or listing is unresolved, but not for a confirmed unlinked
  // account: its listing query is disabled and ordinary sharing must remain available. While
  // listing is loading, Workspace must wait too because a Community listing requires Public.
  const isListingUnresolved = shouldReadListing && isOrgScopedPublishingEnabled && !isFigmaUnlinked && listing == null;
  const isAwaitingCommunityStatus = toolPublishEnabled === true && isListingUnresolved && !shouldFetchCommunityMetadata;
  const isPrivateAccessLocked = isOrgAccessLocked || isListingUnresolved;
  const isVisibilityOptionLocked = (visibility: ToolVisibilityType) => {
    if (visibility === ToolVisibilityType.Public) return false;
    if (isListingDestinationHidden) return true;
    if (visibility === ToolVisibilityType.Private && isPrivateAccessLocked) return true;
    if (isOrgAccessLocked) return false;
    return isCommunityAccessLocked || isAwaitingCommunityStatus;
  };
  const publishKeys = I18N_KEYS.SHARE_WORKFLOW_MODAL.PUBLISH_TO_FIGMA_COMMUNITY;
  const accessLockTooltip = (() => {
    if (isOrgAccessLocked) return t(publishKeys.ORG_DESTINATION.ACCESS_LOCKED_TOOLTIP);
    if (isCommunityAccessLocked) return t(publishKeys.ACCESS_LOCKED_TOOLTIP);
    return '';
  })();

  /**
   * The menu's `disabled` state is only as fresh as the last metadata read, and on a cold open that
   * read hasn't landed yet — so a narrowing option stays clickable for a moment on a tool that is live
   * in Community. Re-read before applying, failing closed to the cached answer, exactly as
   * `ShareWorkflowModal` does.
   */
  const refetchIsCommunityAccessLocked = useCallback(async () => {
    if (
      isFigmaUnlinked ||
      toolPublishEnabled !== true ||
      (listing != null && listing.destination?.type !== 'community')
    ) {
      return false;
    }

    try {
      const result = await refetchCommunityMetadata();
      return result.error ? isPublishedToCommunity(communityMetadata) : isPublishedToCommunity(result.data);
    } catch (e) {
      logger.error(`Error refreshing Community metadata before updating tool access: ${sanitizeError(e)}`);
      return isPublishedToCommunity(communityMetadata);
    }
  }, [communityMetadata, isFigmaUnlinked, listing, refetchCommunityMetadata, toolPublishEnabled]);

  const handleSelectVisibility = async (index: number) => {
    setVisibilityAnchorEl(null);
    const type = visibilityOptions[index].type;
    if (type === tool?.visibility) return;
    if (isListingDestinationHidden && type !== ToolVisibilityType.Public) return;
    if (isListingUnresolved && type === ToolVisibilityType.Private) return;
    if (isAwaitingCommunityStatus && type !== ToolVisibilityType.Public) return;

    if (
      (isOrgAccessLocked && type === ToolVisibilityType.Private) ||
      (type !== ToolVisibilityType.Public && (await refetchIsCommunityAccessLocked()))
    ) {
      updateSnackbarData({
        text: isOrgAccessLocked ? accessLockTooltip : t(publishKeys.SNACKBAR.ACCESS_LOCKED_WHILE_PUBLISHED),
        isOpen: true,
      });
      return;
    }

    setVisibilityMutation.mutate(type);
  };

  const handleClose = () => {
    setEmails([]);
    setCurrentInput({ value: '', isValid: false });
    onClose();
  };

  const handleShare = async () => {
    const toShare = [...emails];
    if (currentInput.value.trim() && currentInput.isValid) toShare.push(currentInput.value.trim());
    if (!toShare.length) return;
    try {
      await shareMutation.mutateAsync(toShare);
      setEmails([]);
      setCurrentInput({ value: '', isValid: false });
      updateSnackbarData({
        text: t(I18N_KEYS.TOOL_SHARE.SUCCESS_MESSAGE),
        isOpen: true,
        icon: <CheckMarkCircleIcon width={20} height={20} />,
      });
    } catch (e) {
      logger.error(`Failed to share tool: ${sanitizeError(e)}`);
      updateSnackbarData({ text: t(I18N_KEYS.TOOL_SHARE.ERROR_MESSAGE), isOpen: true, icon: null, severity: 'error' });
    }
  };

  const handleRemove = (email: string) => {
    removeMutation.mutate(email, { onError: (e) => logger.error(`Failed to remove shared user: ${sanitizeError(e)}`) });
  };

  const canShare = emails.length > 0 || currentInput.isValid;
  const users = orderBy(shared?.users ?? [], 'email', 'asc');

  const accessLevelRow = (
    <ToolShareRow
      icon={selectedVisibility.icon}
      label={<TextSmRg noWrap>{selectedVisibility.text}</TextSmRg>}
      trail={
        <FlexCenVer sx={{ height: 24, px: 0.5 }}>
          <ChevronRightIcon width={16} height={16} />
        </FlexCenVer>
      }
      onClick={(event) => setVisibilityAnchorEl(event.currentTarget)}
      aria-haspopup="menu"
      aria-expanded={Boolean(visibilityAnchorEl)}
    />
  );

  const renderUserRow = (user: ShareRowUser, isCurrentUser: boolean) => {
    const name = user.displayName || user.email || '';
    return (
      <ToolShareRow
        // Role-scoped: the owner renders above the share list and can also appear inside it.
        key={`${isCurrentUser ? 'owner' : 'shared'}:${user.email ?? name}`}
        hoverable
        icon={
          <Avatar aria-label={name} src={user.photoURL || undefined} sx={{ width: 24, height: 24 }}>
            {name || undefined}
          </Avatar>
        }
        label={
          <TextSmRg noWrap>
            {name}
            {isCurrentUser && ` ${t(I18N_KEYS.GENERAL.YOU_PARENS)}`}
          </TextSmRg>
        }
        trail={
          isCurrentUser ? (
            <FlexCenVer sx={{ height: 24, px: 1 }}>
              <TextSmRg>{t(I18N_KEYS.SHARE_WORKFLOW_MODAL.OWNER)}</TextSmRg>
            </FlexCenVer>
          ) : (
            <ButtonContained mode="text" size="xs" onClick={() => user.email && handleRemove(user.email)}>
              {t(I18N_KEYS.SHARE_WORKFLOW_MODAL.REMOVE)}
            </ButtonContained>
          )
        }
      />
    );
  };

  return (
    <AppDialog
      data-testid="share-tool-modal"
      open={open}
      onClose={handleClose}
      width={480}
      slotProps={{ paper: { sx: { background: 'transparent', '&&': { border: 'none' } } } }}
    >
      <Box sx={showPublishPanel ? { '& > *:last-child': { mt: 1 } } : undefined}>
        <AppDialogSection
          sx={{
            position: 'relative',
            background: fplColor.bg,
            border: `1px solid ${EL_COLORS.BoxBorder}`,
            borderRadius: 2,
          }}
        >
          {/* `justifyContent` is what selects FPL's custom header, which brings its own padding and
            close button; everything else here dresses the legacy `DialogTitle`, which brings
            neither — hence the row's own padding, divider and inline close button. */}
          <AppDialogHeader
            component="div"
            sx={{
              px: 2,
              py: 1.5,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              borderBottom: `1px solid ${EL_COLORS.BoxBorder}`,
            }}
          >
            <AppDialogTitle>{t(I18N_KEYS.TOOL_SHARE.TITLE)}</AppDialogTitle>
            <Flex sx={{ gap: 0.5, alignItems: 'center' }}>
              {tool?.canViewWorkflow && (
                <ButtonContained
                  mode="text"
                  size="small"
                  // FPL draws these from a 24px box whose glyph is inset; the legacy SVGs fill theirs,
                  // so they are sized down to sit with the label instead of towering over it.
                  startIcon={<GoToMainComponentIcon width={headerGlyphSize} height={headerGlyphSize} />}
                  onClick={() => window.open(toolWorkflowPath(tool.workflowId), '_blank', 'noopener')}
                >
                  {t(I18N_KEYS.TOOL_VIEW.OPEN_WORKFLOW)}
                </ButtonContained>
              )}
              <CopyBtnV2
                text={`${window.location.origin}/tool/${toolId}`}
                variant="text"
                label={t(I18N_KEYS.GENERAL.COPY_LINK)}
                startIcon={<LinkSmallIcon width={headerGlyphSize} height={headerGlyphSize} />}
                eventTracking={{ name: 'share_tool_modal_copy_link', payload: { visibility: tool?.visibility } }}
              />
              {/* Renders nothing under FPL, whose header owns the close button. */}
              <AppDialogXBtn onClick={handleClose} size={20} />
            </Flex>
          </AppDialogHeader>
          <AppDialogContent sx={{ p: 0 }}>
            {/* Only for someone who can reach the workflow — the note sends them to a control that is
              otherwise not theirs to open. The mirror in `ShareWorkflowModal` gates on the tool. Inside
              the body, because FPL's modal grid puts anything else beside it, in the sidebar column. */}
            {tool?.canViewWorkflow && <ToolShareScopeBanner exposesWorkflow={exposesWorkflow} />}
            {/* The placeholder is the one piece of body copy the design sets at weight 400. */}
            <Flex sx={{ gap: 1, p: 2, '& input::placeholder': { fontWeight: 400 } }}>
              <EmailChipsInput
                emails={emails}
                onSetEmails={setEmails}
                onInputChange={(value, isValid) => setCurrentInput({ value, isValid })}
                placeholder={t(I18N_KEYS.TOOL_SHARE.EMAIL_PLACEHOLDER)}
                inputW="100%"
              />
              <ButtonContained
                mode="filled-light"
                size="medium"
                onClick={() => void handleShare()}
                disabled={!canShare || shareMutation.isPending}
              >
                {t(I18N_KEYS.TOOL_SHARE.INVITE_BUTTON)}
              </ButtonContained>
            </Flex>

            <FlexCol sx={{ pb: 1 }}>
              <FlexCenVer sx={{ height: 24, px: 2 }}>
                {/* The FPL token resolves against a light surface off the FPL theme, which on this
                  dark card reads as near-black, so the legacy path takes the Weave grey. */}
                <TextStdMd color={isFpl ? fplColor.textSecondary : color.White64_T}>
                  {t(I18N_KEYS.TOOL_SHARE.ACCESS_TITLE)}
                </TextStdMd>
              </FlexCenVer>

              {canManageVisibility && (
                <>
                  {accessLockTooltip ? (
                    <Tooltip
                      title={accessLockTooltip}
                      // `ToolShareRow` is not a `forwardRef`, so the tooltip anchors to a wrapper.
                      // `describeChild` keeps it a description: MUI otherwise renames the row through
                      // `aria-label`, which would read out the lock instead of the access level.
                      describeChild
                    >
                      <Box>{accessLevelRow}</Box>
                    </Tooltip>
                  ) : (
                    accessLevelRow
                  )}
                  <AppContextMenu
                    width="260px"
                    open={Boolean(visibilityAnchorEl)}
                    anchorEl={visibilityAnchorEl}
                    onClose={() => setVisibilityAnchorEl(null)}
                    items={visibilityOptions.map((o, i) => ({
                      biEventKey: { menu: MENU_NAMES.TOOL_SHARE, action: 'change_visibility' },
                      name: o.text,
                      action: () => void handleSelectVisibility(i),
                      icon: o.icon,
                      selected: o.type === tool?.visibility,
                      // Keep the access required by the saved destination: Public for Community,
                      // Team or Public for an org.
                      disabled: isVisibilityOptionLocked(o.type),
                    }))}
                  />
                </>
              )}

              <FlexCol sx={{ maxHeight: 320, overflowY: 'auto' }}>
                {/* Workflow editors can open this modal too, so only the creator gets the Owner row. */}
                {tool?.isOwner && currentUser && renderUserRow(currentUser, true)}
                {isLoading ? (
                  <FlexCol sx={{ px: 1, gap: 0.5 }}>
                    {[0, 1].map((i) => (
                      <Skeleton key={i} animation="wave" variant="rounded" width="100%" height={32} />
                    ))}
                  </FlexCol>
                ) : isError ? (
                  <Box sx={{ px: 2 }}>
                    <TextSmRg color={fplColor.textDanger}>{t(I18N_KEYS.GENERAL.UNKNOWN_ERROR)}</TextSmRg>
                  </Box>
                ) : (
                  users.map((u) => renderUserRow(u, false))
                )}
              </FlexCol>
            </FlexCol>
          </AppDialogContent>
        </AppDialogSection>

        {showPublishPanel && (
          <AppDialogSection
            withBody
            sx={{
              background: fplColor.bg,
              border: `1px solid ${EL_COLORS.BoxBorder}`,
              borderRadius: 2,
              p: '8px 0',
            }}
          >
            <StandalonePublishWorkflowProvider value={inWorkflowEditor ? null : standalonePublishWorkflow}>
              <PublishToCommunityPanel
                isPublishingDisabled={!canOfferPublish}
                eligibilityReason={publishEligibility.reason}
                canShowUpgradeUpsell={publishEligibility.canShowUpgradeUpsell}
                canPublish={publishEligibility.canPublish}
                isResolvingEligibility={
                  publishEligibility.isLoading || orgLookupStatus === 'loading' || isLoadingToolPagePublishWorkflow
                }
                source="tool_share_modal"
                tool={{ weaveAppId: toolId }}
                rowLayout="compact"
                orgDestinations={hasOrgOption ? orgDestinations : undefined}
                orgLookupStatus={hasOrgOption ? orgLookupStatus : undefined}
              />
            </StandalonePublishWorkflowProvider>
          </AppDialogSection>
        )}
      </Box>
    </AppDialog>
  );
};

export default ToolShareModal;
